import React from 'react';
import styles from './ProgressBar.module.css';

function ProgressBar({ 
  maxValue,
  currentValue,
  className = '', 
  displayMode='value', 
  variant='default', 
  reverseColor=false, 
  greenThreshold=70,
  yellowThreshold=100,
  orangeThreshold=120,
  redThreshold=150
}) {
  let visualPercentage = 0;    // Percentage for display on the bar (e.g., 56% from 0.56)
  let comparisonPercentage = 0; // Percentage for (FB / pFB) * 100 for color logic
  let barFillPercentage = 0;   // Percentage for the bar's actual fill width (0-100)

  // --- Calculations for percentages ---
  if (displayMode === 'valuePercent') {
    visualPercentage = Math.round(currentValue * 100);
  } else if (maxValue > 0 && currentValue >= 0) { // Ensure currentValue >=0 for this path
    visualPercentage = Math.round((currentValue / maxValue) * 100);
  } else if (currentValue > 0 && maxValue <= 0) { // Positive currentValue with non-positive maxValue
    visualPercentage = Infinity; // Indicate over budget or undefined state
  } else { // Default for other cases (e.g. currentValue = 0)
    visualPercentage = 0;
  }

  if (maxValue > 0 && currentValue > 0) { // Used for comparisonPercentage logic for most variants
    comparisonPercentage = (currentValue / maxValue) * 100;
    barFillPercentage = Math.max(0, Math.min(100, comparisonPercentage));
  } else if (currentValue > 0 && maxValue <= 0) { // FB positive, pFB zero/negative
    comparisonPercentage = Infinity;
    barFillPercentage = 100; // Fill bar fully for critical
  } else if (currentValue === 0 && maxValue >= 0) { // If currentValue is 0, comparison is 0, bar is 0
    comparisonPercentage = 0;
    barFillPercentage = 0;
  } else { // Handles negative currentValue or other edge cases not explicitly covered
    comparisonPercentage = 0;
    barFillPercentage = 0;
  }
  // --- End Percentage Calculations ---
  
  let determinedColorClass = "";

  // --- Main Color Logic ---
  if (variant === 'default' && !reverseColor) {
    // Updated Rules for Fee Burn (variant='default')
    // Using percentage points: "10% above pFB" means pFB + 10 percentage points
    if (currentValue >= 1.0) { // Rule: Red – FB is equal to or greater than 100% (absolute value)
      determinedColorClass = styles.red;
    } else if (maxValue <= 0 && currentValue > 0) { // Critical Edge Case: Positive FB with zero/negative pFB
      determinedColorClass = styles.red;
    } else if (currentValue < 1.0 && (currentValue - maxValue) > 0.10 && maxValue > 0) { // Rule: Amber (FB < 100% and >10 percentage points above pFB)
      determinedColorClass = styles.yellow;
    } else if ( (currentValue < 1.0 && (currentValue - maxValue) <= 0.10 && maxValue > 0) || 
              (currentValue === 0 && maxValue === 0) ||
              (currentValue === 0 && maxValue > 0) 
            ) { // Rule: Green (FB < 100% and ≤10 percentage points above pFB, or 0/0, or 0/positive)
      determinedColorClass = styles.green;
    } else { // Fallback for default variant
      determinedColorClass = styles.green;
    }
  } 
  // --- Original Blue Variant Logic ---
  else if (variant === 'blue') {
    const localBlueThresholds = [20, 40, 60, 80]; // Original hardcoded thresholds
    // Original blue logic applied color only if maxValue > 0 AND currentValue > 0.
    if (maxValue > 0 && currentValue > 0) {
      // comparisonPercentage is (currentValue / maxValue) * 100
      if (comparisonPercentage < localBlueThresholds[0]) determinedColorClass = styles.blueExtraLight;
      else if (comparisonPercentage < localBlueThresholds[1]) determinedColorClass = styles.blueLight;
      else if (comparisonPercentage < localBlueThresholds[2]) determinedColorClass = styles.blueMedium;
      else if (comparisonPercentage < localBlueThresholds[3]) determinedColorClass = styles.blueDark;
      else determinedColorClass = styles.blueDarker;
    }
    // If not (maxValue > 0 && currentValue > 0), determinedColorClass remains "", so no blue style.
  } 
  // --- Original Reverse Color Logic ---
  else if (reverseColor) {
    // Uses greenThreshold, yellowThreshold, orangeThreshold, redThreshold props from component call
    if (maxValue > 0 && currentValue > 0) { // Original condition
      if (comparisonPercentage < redThreshold) determinedColorClass = styles.red; 
      else if (comparisonPercentage < orangeThreshold) determinedColorClass = styles.orange;
      else if (comparisonPercentage < yellowThreshold) determinedColorClass = styles.yellow;
      else determinedColorClass = styles.green;
    }
    // If not (maxValue > 0 && currentValue > 0), determinedColorClass remains "".
  } 
  // --- Fallback to Original Prop-Based Threshold Logic for other variants or combinations ---
  else {
    if (maxValue > 0 && currentValue > 0) { // Original condition
        if (comparisonPercentage < greenThreshold) determinedColorClass = styles.green;
        else if (comparisonPercentage < yellowThreshold) determinedColorClass = styles.yellow;
        else if (comparisonPercentage < orangeThreshold) determinedColorClass = styles.orange;
        else determinedColorClass = styles.red;
    }
    // If not (maxValue > 0 && currentValue > 0), determinedColorClass remains "".
  }
  // --- End Color Logic ---

  const colorClass = determinedColorClass;

  const displayValue = displayMode === 'percentage'
    ? `${Math.round(comparisonPercentage)}%` 
    : displayMode === 'value'
      ? `${currentValue}`
      : displayMode === 'currency'
      ? `£${Number(currentValue).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
      : displayMode === 'valuePercent'
      ? `${visualPercentage}%`
      : '';                        

  return (
    <div className={`${styles.progressBar} ${className}`}>
      <div className={`${styles.progressBar__colorFill} ${colorClass}`} 
          style={{ width: `${barFillPercentage}%` }} 
      />
      <span className={styles.progressBar__text}>
        {displayValue}
      </span>
    </div>
  );
}

export default ProgressBar;
